import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { registerUser } from '../../api/auth';
import { useNotifications } from '../../hooks/useNotifications';
import { t } from '../../utils/translations';
import { registerScreenStyles } from './RegisterScreen.styles';

const RegisterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    firstName: false,
    lastName: false,
    password: false,
    passwordConfirmation: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showError, showSuccess } = useNotifications();
  const navigate = useNavigate();

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return t('register.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return t('register.emailInvalid');
    return null;
  };

  const validateFirstName = (value: string): string | null => {
    if (!value.trim()) return t('register.firstNameRequired');
    if (value.length < 2) return t('register.firstNameMinLength');
    if (value.length > 200) return t('register.firstNameMaxLength');
    return null;
  };

  const validateLastName = (value: string): string | null => {
    if (!value.trim()) return t('register.lastNameRequired');
    if (value.length < 2) return t('register.lastNameMinLength');
    if (value.length > 200) return t('register.lastNameMaxLength');
    return null;
  };

  const validatePassword = (value: string): string | null => {
    if (!value) return t('register.passwordRequired');
    if (value.length < 8) return t('register.passwordMinLength');

    const hasLowerCase = /[a-z]/.test(value);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasDigit = /\d/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);

    if (!hasLowerCase) return t('register.passwordLowerCase');
    if (!hasUpperCase) return t('register.passwordUpperCase');
    if (!hasDigit) return t('register.passwordDigit');
    if (!hasSpecialChar) return t('register.passwordSpecialChar');

    return null;
  };

  const validatePasswordConfirmation = (value: string): string | null => {
    if (!value) return t('register.passwordConfirmationRequired');
    if (value !== password) return t('register.passwordMismatch');
    return null;
  };

  const emailError = touched.email ? validateEmail(email) : null;
  const firstNameError = touched.firstName ? validateFirstName(firstName) : null;
  const lastNameError = touched.lastName ? validateLastName(lastName) : null;
  const passwordError = touched.password ? validatePassword(password) : null;
  const passwordConfirmationError = touched.passwordConfirmation
    ? validatePasswordConfirmation(passwordConfirmation)
    : null;

  const isFormValid =
    !validateEmail(email) &&
    !validateFirstName(firstName) &&
    !validateLastName(lastName) &&
    !validatePassword(password) &&
    !validatePasswordConfirmation(passwordConfirmation);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      email: true,
      firstName: true,
      lastName: true,
      password: true,
      passwordConfirmation: true,
    });

    if (!isFormValid) {
      return;
    }

    if (!executeRecaptcha) {
      showError(t('auth.recaptchaFailed'), t('common.error'));
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await executeRecaptcha('register');

      const response = await registerUser({
        email,
        firstName,
        lastName,
        password,
        passwordConfirmation,
        recaptchaToken,
      });

      // Map API message to translation key
      const messageMap: Record<string, string> = {
        'User registered successfully. Check your email to activate your account.': 'apiMessages.user_registered_check_email',
      };

      const translationKey = messageMap[response.message] || response.message;
      const translatedMessage = translationKey.startsWith('apiMessages.')
        ? t(translationKey)
        : response.message;

      showSuccess(translatedMessage, t('common.success'));

      // Redirect to login after successful registration
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.registrationFailed');
      showError(errorMessage, t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlur = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <Container maxWidth="sm" sx={registerScreenStyles.container}>
      <Paper elevation={3} sx={registerScreenStyles.paper}>
        <Box sx={registerScreenStyles.content}>
          <Typography variant="h4" component="h1" color={"primary"} sx={registerScreenStyles.title}>
            {t('register.title')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={registerScreenStyles.form} noValidate>
            <TextField
              fullWidth
              label={t('register.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur('email')}
              error={!!emailError}
              helperText={emailError}
              sx={registerScreenStyles.input}
            />

            <TextField
              fullWidth
              label={t('register.firstName')}
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => handleBlur('firstName')}
              error={!!firstNameError}
              helperText={firstNameError}
              sx={registerScreenStyles.input}
            />

            <TextField
              fullWidth
              label={t('register.lastName')}
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => handleBlur('lastName')}
              error={!!lastNameError}
              helperText={lastNameError}
              sx={registerScreenStyles.input}
            />

            <TextField
              fullWidth
              label={t('register.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              error={!!passwordError}
              helperText={passwordError || t('register.passwordHint')}
              sx={registerScreenStyles.input}
            />

            <TextField
              fullWidth
              label={t('register.passwordConfirmation')}
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              onBlur={() => handleBlur('passwordConfirmation')}
              error={!!passwordConfirmationError}
              helperText={passwordConfirmationError}
              sx={registerScreenStyles.input}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={registerScreenStyles.submitButton}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('register.submitButton')}
            </Button>

            <Box sx={registerScreenStyles.loginLink}>
              <Typography variant="body2">
                {t('register.hasAccount')}{' '}
                <Link href="/login" underline="hover">
                  {t('register.loginLink')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

const RegisterScreen: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;

  if (!recaptchaSiteKey) {
    return (
      <Container>
        <Typography color="error">
          {t('auth.recaptchaNotConfigured')}
        </Typography>
      </Container>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <RegisterForm />
    </GoogleReCaptchaProvider>
  );
};

export default RegisterScreen;
